import dayjs from 'dayjs';
import _ from 'lodash';

import { MODES_REGLEMENTS } from '@/constants';
import type {
  DepassementTitreType,
  LigneRepartitionType,
  ModeReglementsPointageType,
  ModeReglementType,
  ReglementRepartitionType,
  ReglementStateType,
  ReglementType,
} from '@/types';
import { formatPayeurLabel } from './payeur';

/**
 * Lignes de l'export CSV du pointage des règlements, réduites aux colonnes demandées.
 *
 * `buildCSV` (@mairistem/tools) lit `ligne[colonne]` tel quel : une colonne absente le fait
 * planter, et `null` sort comme le texte « object ». Les valeurs que le tableau calcule à
 * l'affichage (date du reçu, payeur, dates formatées) sont donc produites ici, et toute valeur
 * absente devient une chaîne vide.
 */
export const formatReglementsPointageToCsv = (
  reglements: ReglementType[],
  colonnes: string[],
): Record<string, unknown>[] =>
  _.map(reglements, (reglement) => {
    const valeurs: Record<string, unknown> = {
      ...reglement,
      dateRecu: reglement.dateCreationRecu
        ? dayjs(reglement.dateCreationRecu).format('DD/MM/YYYY')
        : null,
      nomPayeur: formatPayeurLabel(reglement.payeur),
      datePaiement: reglement.datePaiement
        ? dayjs(reglement.datePaiement).format('DD/MM/YYYY HH:mm')
        : null,
      dateValidite: reglement.dateValidite
        ? dayjs(reglement.dateValidite).format('DD/MM/YYYY')
        : null,
    };

    return _.fromPairs(_.map(colonnes, (colonne) => [colonne, valeurs[colonne] ?? '']));
  });

/**
 * Complète chaque règlement saisi du code de son mode : la répartition et le contrôle des titres
 * de paiement en ont besoin, la saisie ne porte que l'identifiant du mode.
 */
export const avecCodeModeReglement = <T extends { identifiantModeReglement?: number }>(
  reglements: T[],
  modesReglements: ModeReglementType[],
): (T & { code?: string })[] =>
  _.map(reglements, (reglement) => ({
    ...reglement,
    code: _.find(modesReglements, { identifiant: reglement.identifiantModeReglement })?.code,
  }));

/** Arrondi au centime, pour comparer des montants issus de sommes flottantes. */
const arrondir = (montant: number) => parseFloat(montant.toFixed(2));

/**
 * Vrai si la ligne peut recevoir un règlement de ce mode. Seuls le CESU et le chèque vacances
 * sont restreints, aux produits dont le drapeau d'éligibilité vaut explicitement `true`.
 */
export const estLigneEligible = (
  ligne: Pick<LigneRepartitionType, 'produit'>,
  code?: string,
): boolean => {
  const titre = MODES_REGLEMENTS.ELIGIBILITE_TITRES[code];

  return !titre || ligne.produit?.[titre.drapeau] === true;
};

/**
 * Contrôle que les titres de paiement saisis (CESU, chèque vacances) tiennent dans les lignes
 * éligibles (REG-861 / REG-862).
 *
 * Chaque titre doit tenir dans le total de ses propres lignes éligibles, et leur cumul dans le
 * total des lignes éligibles à l'un ou l'autre : à deux titres, ces trois conditions suffisent
 * pour qu'une répartition valide existe, même quand un produit n'est éligible qu'à l'un d'eux.
 *
 * @returns les dépassements constatés, vide si la saisie est répartissable.
 */
export const verifierEligibiliteTitres = (
  lignes: LigneRepartitionType[],
  reglements: ReglementRepartitionType[],
): DepassementTitreType[] => {
  const codes = _.keys(MODES_REGLEMENTS.ELIGIBILITE_TITRES);
  const plafond = (eligible: (ligne: LigneRepartitionType) => boolean) =>
    arrondir(_.sumBy(_.filter(lignes, eligible), 'montant'));

  const depassements: DepassementTitreType[] = [];
  let montantTitres = 0;

  _.forEach(codes, (code) => {
    const montant = arrondir(_.sumBy(_.filter(reglements, { code }), (r) => r.montant || 0));
    const plafondMode = plafond((ligne) => estLigneEligible(ligne, code));

    montantTitres += montant;

    if (montant > plafondMode) {
      depassements.push({
        libelle: MODES_REGLEMENTS.ELIGIBILITE_TITRES[code].libelle,
        montant,
        plafond: plafondMode,
      });
    }
  });

  const plafondCumul = plafond((ligne) => _.some(codes, (code) => estLigneEligible(ligne, code)));

  if (_.isEmpty(depassements) && arrondir(montantTitres) > plafondCumul) {
    depassements.push({
      libelle: _.map(MODES_REGLEMENTS.ELIGIBILITE_TITRES, 'libelle').join(' et '),
      montant: arrondir(montantTitres),
      plafond: plafondCumul,
    });
  }

  return depassements;
};

/**
 * Répartit le montant de chaque règlement sur les lignes, au prorata de leur montant de référence.
 *
 * Les titres de paiement (CESU, chèque vacances) sont servis en premier, et uniquement sur les
 * lignes éligibles : d'abord celles éligibles à ce seul titre, puis celles que les deux titres se
 * disputent. Les autres règlements se partagent ensuite, au prorata, ce qu'il reste de chaque
 * ligne — sans titre, c'est la répartition au prorata du montant de chaque ligne.
 *
 * Chaque règlement est affecté puis arrondi au centime, son reliquat d'arrondi reporté sur la
 * dernière ligne qu'il sert, et ce qui reste à servir de chaque ligne est décompté sur ces montants
 * arrondis : chaque règlement retombe exactement sur son montant — ce que l'API valide — et, en
 * paiement complet, chaque ligne exactement sur le sien. Une ligne déjà soldée ne reçoit rien, ni
 * une ligne non éligible un titre. Un titre supérieur au total de ses lignes éligibles n'est servi
 * qu'à hauteur de ce total : `verifierEligibiliteTitres` bloque cette saisie en amont.
 *
 * @returns { cléDeLigne: { identifiantDeReglement: montantAffecté } }
 */
export const repartirAuProrata = (
  lignes: LigneRepartitionType[],
  reglements: ReglementRepartitionType[],
  total: number,
): Record<string, Record<string, number>> => {
  const repartition: Record<string, Record<string, number>> = {};
  // Ce que chaque ligne peut encore recevoir, à mesure que les règlements la servent.
  const capacites: Record<string, number> = {};

  _.forEach(lignes, (ligne) => {
    repartition[ligne.cle] = {};
    capacites[ligne.cle] = ligne.montant;

    _.forEach(reglements, (reglement) => {
      repartition[ligne.cle][reglement.identifiant] = 0;
    });
  });

  // Un total nul (facture soldée, produit gratuit) : rien à répartir.
  if (!total) return repartition;

  // Affecte un montant aux lignes d'un groupe au prorata de ce qu'il leur reste à recevoir.
  const affecter = (
    identifiant: string | number,
    montant: number,
    groupe: LigneRepartitionType[],
  ) => {
    const capaciteGroupe = _.sumBy(groupe, (ligne) => capacites[ligne.cle]);

    if (!(montant > 0) || !(capaciteGroupe > 0)) return;

    const parts = _.map(groupe, (ligne) => ({
      cle: ligne.cle,
      part: arrondir((montant * capacites[ligne.cle]) / capaciteGroupe),
    }));
    const derniere = _.findLast(parts, 'part');

    if (derniere) {
      derniere.part = arrondir(derniere.part + montant - _.sumBy(parts, 'part'));
    }

    _.forEach(parts, ({ cle, part }) => {
      repartition[cle][identifiant] = arrondir(repartition[cle][identifiant] + part);
      capacites[cle] = arrondir(capacites[cle] - part);
    });
  };

  const [titres, autres] = _.partition(reglements, ({ code }) =>
    _.has(MODES_REGLEMENTS.ELIGIBILITE_TITRES, code),
  );
  const codesTitres = _.uniq(_.map(titres, 'code'));

  _.forEach(_.keys(MODES_REGLEMENTS.ELIGIBILITE_TITRES), (code) => {
    const autresCodes = _.without(codesTitres, code);
    // Lignes éligibles à ce seul titre, puis lignes éligibles aussi à un autre titre saisi.
    const groupes = _.partition(
      _.filter(lignes, (ligne) => estLigneEligible(ligne, code)),
      (ligne) => !_.some(autresCodes, (autreCode) => estLigneEligible(ligne, autreCode)),
    );

    _.forEach(_.filter(titres, { code }), (reglement) => {
      let reste = reglement.montant || 0;

      _.forEach(groupes, (groupe) => {
        const montant = Math.min(reste, arrondir(_.sumBy(groupe, (ligne) => capacites[ligne.cle])));

        affecter(reglement.identifiant, montant, groupe);
        reste = arrondir(reste - Math.max(montant, 0));
      });
    });
  });

  _.forEach(autres, (reglement) => {
    affecter(reglement.identifiant, reglement.montant, lignes);
  });

  return repartition;
};

/**
 * Normalise un règlement saisi pour le corps d'une requête d'encaissement : payeur, mode,
 * champs saisis (hors identifiant local) et date de validité au format attendu par l'API.
 *
 * L'appelant y ajoute son affectation aux lignes — `lignesFacture` pour un encaissement de
 * facture, `lignes` au comptant — et doit le faire en dernier pour conserver l'ordre des clés.
 */
export const formatReglementBody = (
  reglement: ReglementStateType,
  identifiantPayeur?: number,
): Record<string, unknown> => {
  const dateValiditeSaisie = (reglement as { dateValidite?: string })?.dateValidite;
  const dateValidite = dateValiditeSaisie
    ? dayjs(dateValiditeSaisie, 'DD/MM/YYYY').format('YYYY-MM-DD')
    : null;

  return {
    identifiantPayeur,
    identifiantModeReglement: reglement?.identifiantModeReglement,
    ..._.omit(reglement, 'identifiant'),
    // Au cas où y'a des tests si la key existe, je préfère pas la mettre si c'est null
    ...(dateValidite && { dateValidite }),
  };
};

// Renvoi true si tous les reglements ont un mode de règlement chèque, numéraire ou virement
export const authorizeRepartAuto = (reglements, modesReglements) =>
  reglements.every((reglement) =>
    modesReglements.some(
      (mode) =>
        mode.code ===
          _.find(modesReglements, { identifiant: reglement.identifiantModeReglement })?.code &&
        [MODES_REGLEMENTS.CHEQUE, MODES_REGLEMENTS.NUMERAIRE, MODES_REGLEMENTS.VIREMENT].includes(
          mode.code,
        ),
    ),
  );

export const getLibelleByCode = (c: ModeReglementsPointageType, pluriel?: boolean) => {
  if (pluriel) {
    switch (c) {
      case MODES_REGLEMENTS.CHEQUE:
        return 'chèques';
      case MODES_REGLEMENTS.NUMERAIRE:
        return '';
      case MODES_REGLEMENTS.CESU:
        return 'CESU';
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return 'chèques vacances';
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return 'cartes bancaires';
      case MODES_REGLEMENTS.VIREMENT:
        return 'virements';
      case MODES_REGLEMENTS.PRELEVEMENT:
        return 'prélèvements';
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return 'cartes bancaires internet';
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return 'prélèvements internet';
      case MODES_REGLEMENTS.TICKET:
        return 'tickets';
      case MODES_REGLEMENTS.BON_CCAS:
        return 'bons CCAS';
      case MODES_REGLEMENTS.BON_CAF:
        return 'bons CAF';
      default:
        return '';
    }
  } else {
    switch (c) {
      case MODES_REGLEMENTS.CHEQUE:
        return 'chèque';
      case MODES_REGLEMENTS.NUMERAIRE:
        return '';
      case MODES_REGLEMENTS.CESU:
        return 'CESU';
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return 'chèque vacances';
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return 'carte bancaire';
      case MODES_REGLEMENTS.VIREMENT:
        return 'virement';
      case MODES_REGLEMENTS.PRELEVEMENT:
        return 'prélèvement';
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return 'carte bancaire internet';
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return 'prélèvement internet';
      case MODES_REGLEMENTS.TICKET:
        return 'ticket';
      case MODES_REGLEMENTS.BON_CCAS:
        return 'bon CCAS';
      case MODES_REGLEMENTS.BON_CAF:
        return 'bon CAF';
      default:
        return '';
    }
  }
};
